import {
  ArrowLeft,
  Bell,
  BookOpen,
  CaretRight,
  ChartLineUp,
  Check,
  CircleNotch,
  Cpu,
  Gear,
  GoogleLogo,
  House,
  Lifebuoy,
  Lightning,
  List,
  Moon,
  PlugsConnected,
  Question,
  Robot,
  RocketLaunch,
  ShieldCheck,
  SignOut,
  SquaresFour,
  Sun,
  WindowsLogo,
  X,
  type IconProps,
} from '@phosphor-icons/react';
import type { ComponentType } from 'react';

import { Configs } from '@/config';

const ICON_MAP = {
  ArrowLeft,
  Bell,
  BookOpen,
  CaretRight,
  ChartLineUp,
  Check,
  CircleNotch,
  Cpu,
  Gear,
  GoogleLogo,
  House,
  Lifebuoy,
  Lightning,
  List,
  Moon,
  PlugsConnected,
  Question,
  Robot,
  RocketLaunch,
  ShieldCheck,
  SignOut,
  SquaresFour,
  Sun,
  WindowsLogo,
  X,
} as const satisfies Record<string, ComponentType<IconProps>>;

export type UnifiedIconName = keyof typeof ICON_MAP;

export interface UnifiedIconProps extends Omit<IconProps, 'size' | 'color' | 'weight'> {
  name: UnifiedIconName;
  size?: number;
  color?: string;
  className?: string;
  weight?: IconProps['weight'];
  'data-testid'?: string;
}

/**
 * Renders an icon from the allowlisted Phosphor set (extend ICON_MAP as needed).
 */
export function UnifiedIcon({
  name,
  size = 24,
  color = 'currentColor',
  className = 'w-6 h-6',
  weight = 'regular',
  'data-testid': dataTestId,
  ...iconProps
}: UnifiedIconProps) {
  const IconComponent = ICON_MAP[name];

  if (!IconComponent) {
    if (Configs.isDev) {
      console.warn(`[UnifiedIcon] Icon "${name}" not found`);
    }
    return null;
  }

  return (
    <IconComponent
      data-testid={dataTestId ?? 'components-unified-icon'}
      size={size}
      color={color}
      weight={weight}
      className={className}
      {...iconProps}
    />
  );
}
